/**
 * useFounder — server-side founder gate.
 *
 * The founder identity is NEVER hardcoded in client code. The signed-in
 * user's email is compared (server-side, in the `founder-check` edge
 * function) against the FOUNDER_EMAIL secret. On match, the function
 * bootstraps the public.founders row (which the tier-pinning trigger uses
 * to flip the profile to tier_attuned permanently) and returns
 * { isFounder: true }.
 *
 * The local cache is keyed by user id only as a UX optimization so the
 * Founder link / onboarding bypass renders without a round-trip on repeat
 * visits. Forging the cache value grants nothing — every privileged action
 * is re-checked server-side via the founders row.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

const cacheKey = (uid: string) => `wavlynq.isFounder.${uid}`;

export const useFounder = () => {
  const { user } = useAuth();
  const [isFounder, setIsFounder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkedUserId, setCheckedUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setIsFounder(false);
      setCheckedUserId(null);
      setLoading(false);
      return;
    }

    // Never set isFounder from cache — only the server response is authoritative.
    // The cache is used solely to keep `loading=true` until the server responds,
    // avoiding a layout flicker for legitimate founders.
    setLoading(true);

    let active = true;
    supabase.functions
      .invoke<{ isFounder: boolean }>("founder-check")
      .then(({ data }) => {
        if (!active) return;
        const ok = !!data?.isFounder;
        setIsFounder(ok);
        setCheckedUserId(user.id);
        try {
          if (ok) localStorage.setItem(cacheKey(user.id), "1");
          else localStorage.removeItem(cacheKey(user.id));
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        if (active) {
          setIsFounder(false);
          setCheckedUserId(user.id);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [user]);

  return { isFounder, loading: loading || (!!user && checkedUserId !== user.id) };
};
